import React, { useEffect } from "react"
import { PageProps, graphql } from "gatsby"
import { useI18next, useTranslation } from "gatsby-plugin-react-i18next"
import { routeParams, routes } from "../../../../../../routes"
import { Layout } from "../../../../../../components/Layout"
import { PageMetadata } from "../../../../../../components/SEO"
import { SpinnerBlock } from "../../../../../../components/SpinnerBlock"
import { Elements } from "@stripe/react-stripe-js"
import getStripe from "../../../../../../util/get-stripe"
import {
  useGetOrder,
  usePaymentMethods,
} from "../../../../../../services/queries"
import { useHandleFetchError } from "../../../../../../util/error-handler"
import { formatNextAction } from "../../../../../../models/payment-util"

import { CardPaymentForm } from "../../../../../../components/checkout/payment/CardPaymentForm"
import { WalletPaymentForm } from "../../../../../../components/checkout/payment/WalletPaymentForm"
import { useNavigateToPaymentStatusScreens } from "../../../../../../hooks/use-navigate-to-payment-status-screens"
import { AdyenSwishWalletQRScannerSSEWrapper } from "../../../../../../components/checkout/payment/AdyenSwishWalletQRScannerSSEWrapper"

const PaymentPage = ({ params, location: { search } }: PageProps) => {
  const { navigate } = useI18next()
  const searchParams = new URLSearchParams(search)
  const paymentType = searchParams.get("type") as
    | "wallet"
    | "card"
    | "card_sca"
    | "swish-wallet"
  const phonenumberPrefix = searchParams.get("phonenumberPrefix")
  const phoneNumber = searchParams.get("phoneNumber")
  const countryCode = params[routeParams.countryCode]
  const slug = params[routeParams.slug]
  const orderId = params[routeParams.orderId]
  const {
    data: order,
    error: orderError,
    isLoading: isOrderLoading,
  } = useGetOrder(orderId)
  const { data: paymentMethods, isLoading: isPaymentMethodsLoading } =
    usePaymentMethods({
      orderId: orderId,
      supportedMethods: {
        adyen_custom: Array.from(new Set(["card"])),
        adyen: Array.from(new Set(["card"])),
      },
    })
  const { showPaymentSucceededScreen, showPaymentFailedScreen } =
    useNavigateToPaymentStatusScreens({
      countryCode,
      slug,
      orderId,
    })

  // We prioritize adyen_custom over adyen because we want to use the custom adyen payment method if it is available
  const adyenPaymentMethod = paymentMethods?.adyen_custom
    ? paymentMethods.adyen_custom
    : paymentMethods?.adyen

  const payments = order?.order?.payments
  const intents = payments && payments[0]?.intents
  const nextAction = intents && intents[0]?.next_action

  const formattedNextAction = nextAction ? formatNextAction(nextAction) : null

  const { t } = useTranslation()

  useEffect(() => {
    if (paymentType) {
      return
    }
    navigate(
      routes.checkout.base({
        countryCode,
        slug,
        orderId,
      })
    )
  }, [paymentType])

  useHandleFetchError(orderError, t)

  const customer = order?.order.customer

  return (
    <Layout
      hideAuth={true}
      countryCode={params[routeParams.countryCode]}
      hideAppCta={true}
    >
      {isOrderLoading ||
      isPaymentMethodsLoading ||
      !order ||
      !formattedNextAction ? (
        <SpinnerBlock />
      ) : (
        <Elements stripe={getStripe()}>
          {paymentType === "wallet" ? (
            <WalletPaymentForm
              order={order}
              providerName={formattedNextAction.providerName}
              showPaymentFailedScreen={showPaymentFailedScreen}
              showPaymentSucceededScreen={showPaymentSucceededScreen}
              src={
                "mobilePayAppRedirectUri" in formattedNextAction
                  ? formattedNextAction.mobilePayAppRedirectUri!
                  : "redirect_url" in formattedNextAction
                    ? formattedNextAction.redirect_url
                    : ""
              }
            />
          ) : paymentType === "swish-wallet" ? (
            <AdyenSwishWalletQRScannerSSEWrapper
              order={order}
              countryCode={countryCode}
              slug={slug}
              showPaymentFailedScreen={showPaymentFailedScreen}
              showPaymentSucceededScreen={showPaymentSucceededScreen}
            />
          ) : (
            <CardPaymentForm
              order={order}
              countryCode={countryCode}
              isSca={paymentType === "card_sca"}
              deliveryAddress={order?.order.delivery_address}
              checkoutFormValues={{
                ...(order?.order.delivery_address &&
                  order?.order.delivery_address),
                ...(customer?.first_name && { firstName: customer.first_name }),
                ...(customer?.email && { email: customer.email }),
                ...(customer?.last_name && { lastName: customer.last_name }),
                ...(phoneNumber && {
                  phoneNumber: phoneNumber,
                }),
                phonenumberPrefix: phonenumberPrefix || "",
              }}
              nextAction={formattedNextAction}
              adyenContext={adyenPaymentMethod}
              hide={() => {
                navigate(
                  routes.checkout.base({
                    countryCode,
                    slug,
                    orderId,
                  })
                )
              }}
              showPaymentFailedScreen={showPaymentFailedScreen}
              showPaymentSucceededScreen={showPaymentSucceededScreen}
            />
          )}
        </Elements>
      )}
    </Layout>
  )
}

export default PaymentPage

export const Head = () => (
  <PageMetadata
    title={process.env.GATSBY_PARTNER_TITLE || ""}
    description={process.env.GATSBY_PARTNER_DESCRIPTION || ""}
    image={process.env.GATSBY_LOGO || ""}
  />
)

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`
